;!function(){try{var e="undefined"!=typeof window?window:"undefined"!=typeof global?global:"undefined"!=typeof globalThis?globalThis:"undefined"!=typeof self?self:{};e._sentryModuleMetadata=e._sentryModuleMetadata||{},e._sentryModuleMetadata[(new e.Error).stack]=Object.assign({},e._sentryModuleMetadata[(new e.Error).stack],{"_sentryBundlerPluginAppKey:qogita-web-sellers":true});}catch(e){}}();import { pluralize } from '@qogita/format/string'
import { Anchor } from '@qogita/ui/v2/anchor'
import { Badge } from '@qogita/ui/v2/badge'
import { ButtonIcon } from '@qogita/ui/v2/button-icon'
import { Icon } from '@qogita/ui/v2/icon'
import { Logo } from '@qogita/ui/v2/logo'
import { differenceInDays } from 'date-fns'
import { EmptyState } from '@qogita/ui/v2/empty-state'
import { PausedSellerReasonModal } from 'src/components/paused-seller-reason-modal'
import { InventoryTable } from 'src/deprecated/components/pages/inventory/_components/InventoryTable'
import { Seo } from 'src/deprecated/components/shared/Seo'
import { useUser } from 'src/deprecated/contexts/User'
import { useGetSaleWithHiddenOffersList } from 'src/deprecated/hooks/api/useGetHiddenOffersList'
import { useGetStocklistSource } from 'src/deprecated/hooks/api/useGetStocklistSource'
import { useGetStocklistSourceNames } from 'src/deprecated/hooks/api/useGetStocklistSourceNames'
import { useGetStocklistSourceOffers } from 'src/deprecated/hooks/api/useGetStocklistSourceOffers'
import { useInventoryQueryParams } from 'src/deprecated/hooks/utils/useInventoryQueryParams'
import { Page as NextPage } from 'src/deprecated/types/next'

import { PageLayoutRebranded } from '../../shared/templates/PageLayoutRebranded'
import { ActiveFiltersBadges } from './_components/ActiveFilterBadges'
import { DownloadInsightsCard } from './_components/DownloadInsightsCard'
import { InventoryBrandsFilter } from './_components/InventoryTable/InventoryBrandsFilter'
import { InventoryStatisticsCard } from './_components/InventoryTable/InventoryStatisticsCards'
import { PriceListSelector } from './_components/PriceListSelector'

function StocklistLastProcessedBadge({
  receivedDaysAgo,
}: {
  receivedDaysAgo: number
}) {
  let label = `Updated: ${receivedDaysAgo} days ago`

  if (receivedDaysAgo === 0) label = `Updated: Today`
  if (receivedDaysAgo === 1) label = `Updated: Yesterday`

  return (
    <Badge emphasis="high" size="small" variant="info">
      {label}
    </Badge>
  )
}

const Page: NextPage = () => {
  const user = useUser()

  const { page, brandsFilter, sort, stocklistQid, setStocklistQid, origin } =
    useInventoryQueryParams()

  const { data: HiddenOffers } = useGetSaleWithHiddenOffersList()
  const hiddenProductsCount =
    HiddenOffers?.reduce((sum, sale) => sum + (sale.productsCount || 0), 0) ?? 0

  const {
    stocklistSources,
    totalResults: stocklistSourcesCount,
    isPending: isStocklistSourcesPending,
    isError: isStocklistSourcesError,
  } = useGetStocklistSourceNames({
    enabled: true,
    filterExpired: true,
  })

  // stocklist via URL if valid, otherwise default to the first one in the list
  const selectedStocklistQid =
    stocklistSources.find((source) => source.qid === stocklistQid)?.qid ??
    stocklistSources[0]?.qid

  const {
    data: selectedStocklistSource,
    isPending: isSelectedStocklistPending,
    isError: isSelectedStocklistError,
  } = useGetStocklistSource({
    sourceQid: selectedStocklistQid,
    enabled: true,
  })

  const isPriceListBeingProcessed =
    selectedStocklistSource && selectedStocklistSource.countGtins === null

  const receivedAt = selectedStocklistSource?.receivedAt
  const receivedDaysAgo =
    receivedAt == null
      ? null
      : differenceInDays(new Date(), new Date(receivedAt))

  const stocklistOffersQuery = useGetStocklistSourceOffers({
    sourceQid: selectedStocklistQid,
    page,
    brands: brandsFilter,
    ordering:
      sort === 'inventory' ||
      sort === '-inventory' ||
      sort === 'originalPrice' ||
      sort === '-originalPrice'
        ? sort
        : undefined,
    enabled:
      selectedStocklistSource?.countGtins != null &&
      selectedStocklistSource?.areInsightsAvailable, // null countGtins / unavailable insights = still processing; undefined source = details still loading
  })

  if (isStocklistSourcesPending || isStocklistSourcesError) {
    return (
      <>
        <Seo title="Your inventory" />
        <PausedSellerReasonModal user={user} />

        <PageLayoutRebranded.Header>
          <PageLayoutRebranded.Title>Your inventory</PageLayoutRebranded.Title>
        </PageLayoutRebranded.Header>
      </>
    )
  }

  // if no stocklists found after query finishes, show empty state
  if (stocklistSourcesCount === 0) {
    return (
      <>
        <Seo title="Your inventory" />
        <PausedSellerReasonModal user={user} />

        <PageLayoutRebranded.Header>
          <PageLayoutRebranded.Title>Your inventory</PageLayoutRebranded.Title>
        </PageLayoutRebranded.Header>

        <PageLayoutRebranded.Body maxWidth="lg">
          <div className="my-32">
            <EmptyState
              header="You have no active price lists"
              description="Update an expired price list or upload a new one to view pricing reports"
              actions={
                <Anchor href="/price-lists/add-price-list/" variant="contained">
                  <Icon
                    name="heroicons/outline/arrow-up-on-square"
                    className="size-4.5"
                  />
                  Upload a price list
                </Anchor>
              }
            />
          </div>
        </PageLayoutRebranded.Body>
      </>
    )
  }

  // Selected price list loading or failed
  if (isSelectedStocklistPending || isSelectedStocklistError) {
    return (
      <>
        <Seo title="Your inventory" />
        <PausedSellerReasonModal user={user} />

        <PageLayoutRebranded.Header>
          <PageLayoutRebranded.Title>Your inventory</PageLayoutRebranded.Title>
        </PageLayoutRebranded.Header>

        <PageLayoutRebranded.Body maxWidth="lg">
          <div className="flex flex-col gap-6">
            <div className="flex flex-wrap items-center gap-3">
              <PriceListSelector
                stocklistSources={stocklistSources}
                value={selectedStocklistQid}
                onValueChange={setStocklistQid}
              />
              <p className="typography-body-xs-normal text-low-emphasis">
                {pluralize({
                  word: 'price list',
                  count: stocklistSourcesCount,
                })}{' '}
                available
              </p>
              <Anchor
                href="/price-lists"
                variant="text"
                size="small"
                className="ml-auto"
              >
                <Icon
                  name="heroicons/outline/cog"
                  className="hidden size-4 lg:block"
                />
                Manage your price lists
                <ButtonIcon name="heroicons/solid/arrow-right" />
              </Anchor>
            </div>
          </div>
        </PageLayoutRebranded.Body>
      </>
    )
  }

  // Selected price list is still processing (offers ingesting or insights not ready yet)
  if (
    isPriceListBeingProcessed ||
    !selectedStocklistSource.areInsightsAvailable
  ) {
    return (
      <>
        <Seo title="Your inventory" />
        <PausedSellerReasonModal user={user} />

        <PageLayoutRebranded.Header>
          <PageLayoutRebranded.Title>Your inventory</PageLayoutRebranded.Title>
        </PageLayoutRebranded.Header>

        <PageLayoutRebranded.Body maxWidth="lg">
          <div className="flex flex-col gap-6">
            <div className="flex flex-wrap items-center gap-3">
              <PriceListSelector
                stocklistSources={stocklistSources}
                value={selectedStocklistQid}
                onValueChange={setStocklistQid}
              />
              <p className="typography-body-xs-normal text-low-emphasis">
                {pluralize({
                  word: 'price list',
                  count: stocklistSourcesCount,
                })}{' '}
                available
              </p>
              <Anchor
                href="/price-lists"
                variant="text"
                size="small"
                className="ml-auto"
              >
                <Icon
                  name="heroicons/outline/cog"
                  className="hidden size-4 lg:block"
                />
                Manage your price lists
                <ButtonIcon name="heroicons/solid/arrow-right" />
              </Anchor>
            </div>
            <div className="my-32 flex grow flex-col items-center justify-center gap-3 p-6 text-center">
              <Logo name="qogita-logomark" className="h-12 w-12" />

              <h3 className="text-high-emphasis typography-header-4xl-bold">
                This price list is processing
              </h3>
              <p className="text-low-emphasis typography-body-base-normal">
                We&apos;ll show your products here as soon as it&apos;s ready.
                Price lists take 20 minutes to update.
              </p>
            </div>
          </div>
        </PageLayoutRebranded.Body>
      </>
    )
  }

  return (
    <>
      <Seo title="Your inventory" />
      <PausedSellerReasonModal user={user} />

      <PageLayoutRebranded.Header>
        <PageLayoutRebranded.Title>Your inventory</PageLayoutRebranded.Title>
      </PageLayoutRebranded.Header>

      <PageLayoutRebranded.Body maxWidth="lg">
        <div className="flex flex-col gap-6">
          <div className="flex flex-wrap items-center gap-3">
            <PriceListSelector
              stocklistSources={stocklistSources}
              value={selectedStocklistQid}
              onValueChange={setStocklistQid}
            />
            <p className="typography-body-xs-normal text-low-emphasis">
              {pluralize({
                word: 'price list',
                count: stocklistSourcesCount,
              })}{' '}
              available
            </p>
            <Anchor
              href="/price-lists"
              variant="text"
              size="small"
              className="ml-auto"
            >
              <Icon
                name="heroicons/outline/cog"
                className="hidden size-4 lg:block"
              />
              Manage your price lists
              <ButtonIcon name="heroicons/solid/arrow-right" />
            </Anchor>
          </div>

          <div className="grid grid-cols-1 gap-6 lg:grid-cols-10">
            <div className="lg:col-span-6">
              {selectedStocklistSource ? (
                <DownloadInsightsCard
                  selectedStocklistSource={selectedStocklistSource}
                  origin={origin}
                />
              ) : null}
            </div>
            <div className="lg:col-span-4">
              <InventoryStatisticsCard
                queryResult={stocklistOffersQuery}
                hiddenProductsCount={hiddenProductsCount}
              />
            </div>
          </div>

          <div>
            <div className="border-default bg-default flex w-full flex-col flex-wrap rounded-lg rounded-b-none border border-b-0 p-4">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <InventoryBrandsFilter />
                {receivedDaysAgo === null ? null : (
                  <StocklistLastProcessedBadge
                    receivedDaysAgo={receivedDaysAgo}
                  />
                )}
              </div>
              <div className="flex flex-wrap gap-2">
                <ActiveFiltersBadges />
              </div>
            </div>
            <InventoryTable
              offers={
                stocklistOffersQuery.error
                  ? []
                  : (stocklistOffersQuery.data?.results ?? [])
              }
              totalResults={stocklistOffersQuery.data?.count ?? 0}
              isLoading={stocklistOffersQuery.isPending}
              isPlaceholderData={stocklistOffersQuery.isPlaceholderData}
            />
          </div>

          <p className="max-w-4xl italic">
            *This list only contains health and beauty products found in your
            stocklist. You can find a full list of products that we haven&apos;t
            included and won&apos;t be shown to buyers in the pricing analysis
            download above the table. If you think we&apos;ve missed a product
            that should be here, let us know through{' '}
            <Anchor
              variant="inline-text"
              size="inline"
              target="_blank"
              href="https://qogita.typeform.com/to/cujTYlHo"
            >
              this form
            </Anchor>
            .
          </p>
        </div>
      </PageLayoutRebranded.Body>
    </>
  )
}

Page.options = {
  getLayout: (page) => (
    <PageLayoutRebranded maxWidth="lg">{page}</PageLayoutRebranded>
  ),
}

export { Page }
